import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  BriefcaseIcon,
  ScissorsIcon,
  TagIcon,
  ShoppingBagIcon,
  CurrencyDollarIcon,
  ArrowUpIcon,
  ArrowTrendingUpIcon,
  StarIcon
} from '@heroicons/react/24/outline';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { Chart as ChartJS, ArcElement, BarElement, CategoryScale, 
  LinearScale, PointElement, LineElement, Title, Tooltip, Legend } from 'chart.js';

// Registrar os componentes necessários do Chart.js
ChartJS.register(
  ArcElement, 
  BarElement,
  CategoryScale, 
  LinearScale, 
  PointElement, 
  LineElement, 
  Title, 
  Tooltip, 
  Legend
);

const ServicesSection = ({ metrics, isMobile }) => {
  const [period, setPeriod] = useState('month');
  const [activeTab, setActiveTab] = useState('services');
  
  // Dados simulados para serviços
  const servicesData = {
    totalServices: 12,
    mostPopular: 'Corte Feminino',
    mostProfitable: 'Coloração',
    avgDuration: 45,
    avgPrice: 120,
    totalProducts: 28,
    topSellingProduct: 'Shampoo Profissional',
    productRevenue: 3200,
    serviceRevenue: 9300,
    totalRevenue: 12500
  };
  
  // Lista de serviços simulada
  const services = [
    {
      id: 1,
      name: 'Corte Feminino',
      price: 80,
      duration: 40,
      appointments: 48,
      revenue: 3840,
      rating: 4.8,
      growth: 12
    },
    {
      id: 2,
      name: 'Coloração',
      price: 150,
      duration: 90,
      appointments: 32,
      revenue: 4800,
      rating: 4.9,
      growth: 18
    },
    {
      id: 3,
      name: 'Corte Masculino',
      price: 60,
      duration: 30,
      appointments: 40,
      revenue: 2400,
      rating: 4.7,
      growth: 8
    },
    {
      id: 4,
      name: 'Manicure',
      price: 50,
      duration: 40,
      appointments: 35,
      revenue: 1750,
      rating: 4.6,
      growth: 5
    },
    {
      id: 5,
      name: 'Pedicure',
      price: 70,
      duration: 50,
      appointments: 30,
      revenue: 2100,
      rating: 4.5,
      growth: 3
    },
    {
      id: 6,
      name: 'Hidratação',
      price: 90,
      duration: 45,
      appointments: 28,
      revenue: 2520,
      rating: 4.8,
      growth: 15
    }
  ];
  
  // Lista de produtos simulada
  const products = [
    {
      id: 1,
      name: 'Shampoo Profissional',
      price: 80,
      stock: 15,
      sold: 18,
      revenue: 1440,
      margin: 40
    },
    {
      id: 2,
      name: 'Condicionador',
      price: 85,
      stock: 12,
      sold: 15,
      revenue: 1275,
      margin: 45
    },
    {
      id: 3,
      name: 'Máscara Capilar',
      price: 95,
      stock: 8,
      sold: 10,
      revenue: 950,
      margin: 50
    },
    {
      id: 4,
      name: 'Óleo Capilar',
      price: 65,
      stock: 20,
      sold: 8,
      revenue: 520,
      margin: 60
    }
  ];
  
  // Dados para o gráfico de serviços por popularidade
  const servicesCountData = {
    labels: services.map(s => s.name),
    datasets: [
      {
        label: 'Quantidade de Agendamentos',
        data: services.map(s => s.appointments),
        backgroundColor: 'rgba(99, 102, 241, 0.7)',
        borderColor: 'rgb(99, 102, 241)',
        borderWidth: 1,
        borderRadius: 4,
      }
    ]
  };
  
  // Dados para o gráfico de serviços por faturamento
  const servicesRevenueData = {
    labels: services.map(s => s.name),
    datasets: [
      {
        label: 'Faturamento (R$)',
        data: services.map(s => s.revenue),
        backgroundColor: 'rgba(16, 185, 129, 0.7)',
        borderColor: 'rgb(16, 185, 129)',
        borderWidth: 1,
        borderRadius: 4,
      }
    ]
  };
  
  // Dados para o gráfico de crescimento de serviços
  const servicesGrowthData = {
    labels: ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun'],
    datasets: [
      {
        label: 'Coloração',
        data: [15, 18, 20, 22, 25, 32],
        borderColor: 'rgb(236, 72, 153)',
        backgroundColor: 'rgba(236, 72, 153, 0.1)',
        tension: 0.4,
        fill: false
      },
      {
        label: 'Corte Feminino',
        data: [30, 32, 35, 40, 45, 48],
        borderColor: 'rgb(59, 130, 246)',
        backgroundColor: 'rgba(59, 130, 246, 0.1)',
        tension: 0.4,
        fill: false
      },
      {
        label: 'Hidratação',
        data: [10, 12, 15, 18, 22, 28],
        borderColor: 'rgb(139, 92, 246)',
        backgroundColor: 'rgba(139, 92, 246, 0.1)',
        tension: 0.4,
        fill: false
      }
    ]
  };
  
  // Dados para o gráfico de distribuição de receita
  const revenueDistributionData = {
    labels: ['Serviços', 'Produtos'],
    datasets: [
      {
        data: [servicesData.serviceRevenue, servicesData.productRevenue],
        backgroundColor: [
          'rgba(59, 130, 246, 0.7)', // azul - serviços
          'rgba(16, 185, 129, 0.7)'  // verde - produtos
        ],
        borderColor: [
          'rgb(59, 130, 246)',
          'rgb(16, 185, 129)'
        ],
        borderWidth: 1,
      }
    ]
  };
  
  // Dados para o gráfico de vendas de produtos
  const productsData = {
    labels: products.map(p => p.name),
    datasets: [
      {
        label: 'Unidades Vendidas',
        data: products.map(p => p.sold),
        backgroundColor: 'rgba(245, 158, 11, 0.7)',
        borderColor: 'rgb(245, 158, 11)',
        borderWidth: 1,
        borderRadius: 4,
      }
    ]
  };

  // Opções para gráficos
  const barOptions = {
    responsive: true,
    plugins: {
      legend: {
        display: false
      }
    },
    scales: {
      y: {
        beginAtZero: true
      }
    },
    maintainAspectRatio: false
  };
  
  const lineOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
        labels: {
          font: {
            size: isMobile ? 10 : 12
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true
      }
    },
    maintainAspectRatio: false
  };
  
  const doughnutOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          font: {
            size: isMobile ? 10 : 12
          }
        }
      }
    },
    cutout: '65%',
    maintainAspectRatio: false
  };

  return (
    <div className="space-y-6">
      <h2 className="text-xl font-bold text-gray-800">Serviços e Produtos</h2>
      
      {/* Abas de navegação */}
      <div className="bg-white rounded-lg p-1 shadow-sm flex">
        <button 
          onClick={() => setActiveTab('services')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium ${
            activeTab === 'services' 
              ? 'bg-indigo-100 text-indigo-800' 
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          <div className="flex items-center justify-center">
            <ScissorsIcon className="h-4 w-4 mr-1.5" />
            <span>Serviços</span>
          </div>
        </button>
        <button 
          onClick={() => setActiveTab('products')}
          className={`flex-1 py-2 px-4 rounded-md text-sm font-medium ${
            activeTab === 'products' 
              ? 'bg-green-100 text-green-800' 
              : 'text-gray-600 hover:text-gray-800'
          }`}
        >
          <div className="flex items-center justify-center">
            <ShoppingBagIcon className="h-4 w-4 mr-1.5" />
            <span>Produtos</span>
          </div>
        </button>
      </div>
      
      {/* Conteúdo da aba de Serviços */}
      {activeTab === 'services' && (
        <>
          {/* Cards de estatísticas */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-500">Total de Serviços</h3>
                <div className="bg-indigo-100 p-2 rounded-lg">
                  <BriefcaseIcon className="h-5 w-5 text-indigo-600" />
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-800">{servicesData.totalServices}</p>
              <p className="mt-1 text-xs text-gray-500">oferecidos atualmente</p>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-500">Mais Popular</h3>
                <div className="bg-blue-100 p-2 rounded-lg">
                  <StarIcon className="h-5 w-5 text-blue-600" />
                </div>
              </div>
              <p className="text-lg font-bold text-gray-800 truncate">{servicesData.mostPopular}</p>
              <div className="mt-1 flex items-center text-xs">
                <span className="text-blue-600 font-medium">48 agendamentos</span>
                <span className="text-gray-400 ml-1 truncate">no período</span>
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-500">Mais Rentável</h3>
                <div className="bg-green-100 p-2 rounded-lg">
                  <CurrencyDollarIcon className="h-5 w-5 text-green-600" />
                </div>
              </div>
              <p className="text-lg font-bold text-gray-800 truncate">{servicesData.mostProfitable}</p>
              <div className="mt-1 flex items-center text-xs">
                <span className="text-green-600 font-medium">R$ 4.800</span>
                <span className="text-gray-400 ml-1 truncate">em receita</span>
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-500">Ticket Médio</h3>
                <div className="bg-purple-100 p-2 rounded-lg">
                  <TagIcon className="h-5 w-5 text-purple-600" />
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-800">R$ {servicesData.avgPrice}</p>
              <div className="mt-1 flex items-center text-xs">
                <ArrowTrendingUpIcon className="h-3 w-3 text-green-500 mr-1" />
                <span className="text-green-500 font-medium">+8%</span>
                <span className="text-gray-400 ml-1">vs. anterior</span>
              </div>
            </motion.div>
          </div>
          
          {/* Gráficos de serviços */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
            >
              <h3 className="text-lg font-semibold mb-4">Serviços por Popularidade</h3>
              <div className="h-64">
                <Bar data={servicesCountData} options={barOptions} />
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
            >
              <h3 className="text-lg font-semibold mb-4">Serviços por Faturamento</h3>
              <div className="h-64">
                <Bar data={servicesRevenueData} options={barOptions} />
              </div>
            </motion.div>
          </div>
          
          {/* Tendência de crescimento e lista de serviços */}
          <div className="grid grid-cols-1 gap-6">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
            >
              <h3 className="text-lg font-semibold mb-4">Tendência de Crescimento</h3>
              <div className="h-72">
                <Line data={servicesGrowthData} options={lineOptions} />
              </div>
              <div className="mt-3 p-3 bg-indigo-50 rounded-lg">
                <p className="text-sm font-medium text-indigo-800">Análise de Crescimento</p>
                <p className="text-xs text-indigo-700 mt-1">
                  A Coloração apresenta a maior taxa de crescimento (+18%), seguido por Hidratação (+15%) 
                  e Corte Feminino (+12%). Invista em promoções destes serviços para potencializar os resultados.
                </p>
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
            >
              <h3 className="text-lg font-semibold mb-4">Todos os Serviços</h3>
              
              <div className="overflow-x-auto">
                <table className="w-full min-w-full divide-y divide-gray-200">
                  <thead>
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Serviço</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Preço</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duração</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Agendamentos</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Avaliação</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Crescimento</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {services.map((service, index) => (
                      <tr key={service.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="font-medium text-gray-900">{service.name}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm text-gray-900">R$ {service.price}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{service.duration} min</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{service.appointments}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center text-amber-500">
                            <span className="text-sm font-medium mr-1">{service.rating}</span>
                            <StarIcon className="h-4 w-4 fill-current" />
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center text-green-600">
                            <ArrowUpIcon className="h-4 w-4 mr-1" />
                            <span className="text-sm font-medium">{service.growth}%</span>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <div className="mt-4 flex justify-end">
                <button className="text-sm text-indigo-600 font-medium hover:text-indigo-700 flex items-center">
                  <span>Gerenciar serviços</span>
                  <ArrowTrendingUpIcon className="h-4 w-4 ml-1 rotate-90" />
                </button>
              </div>
            </motion.div>
          </div>
        </>
      )}
      
      {/* Conteúdo da aba de Produtos */}
      {activeTab === 'products' && (
        <>
          {/* Cards de estatísticas de produtos */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-500">Total de Produtos</h3>
                <div className="bg-green-100 p-2 rounded-lg">
                  <ShoppingBagIcon className="h-5 w-5 text-green-600" />
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-800">{servicesData.totalProducts}</p>
              <p className="mt-1 text-xs text-gray-500">em catálogo</p>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-500">Mais Vendido</h3>
                <div className="bg-amber-100 p-2 rounded-lg">
                  <ShoppingBagIcon className="h-5 w-5 text-amber-600" />
                </div>
              </div>
              <p className="text-lg font-bold text-gray-800 truncate">{servicesData.topSellingProduct}</p>
              <div className="mt-1 flex items-center text-xs">
                <span className="text-amber-600 font-medium">18 unidades</span>
                <span className="text-gray-400 ml-1 truncate">vendidas</span>
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-500">Receita de Produtos</h3>
                <div className="bg-blue-100 p-2 rounded-lg">
                  <CurrencyDollarIcon className="h-5 w-5 text-blue-600" />
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-800">R$ {servicesData.productRevenue}</p>
              <div className="mt-1 flex items-center text-xs">
                <ArrowTrendingUpIcon className="h-3 w-3 text-green-500 mr-1" />
                <span className="text-green-500 font-medium">+12%</span>
                <span className="text-gray-400 ml-1">vs. anterior</span>
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
            >
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-medium text-gray-500">Margem Média</h3>
                <div className="bg-purple-100 p-2 rounded-lg">
                  <TagIcon className="h-5 w-5 text-purple-600" />
                </div>
              </div>
              <p className="text-2xl font-bold text-gray-800">48%</p>
              <div className="mt-1 flex items-center text-xs">
                <ArrowTrendingUpIcon className="h-3 w-3 text-green-500 mr-1" />
                <span className="text-green-500 font-medium">+5%</span>
                <span className="text-gray-400 ml-1">vs. anterior</span>
              </div>
            </motion.div>
          </div>
          
          {/* Gráficos de produtos */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
            >
              <h3 className="text-lg font-semibold mb-4">Produtos Mais Vendidos</h3>
              <div className="h-64">
                <Bar data={productsData} options={barOptions} />
              </div>
            </motion.div>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6 }}
              className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
            >
              <h3 className="text-lg font-semibold mb-4">Distribuição de Receita</h3>
              <div className="h-64 flex items-center justify-center">
                <Doughnut data={revenueDistributionData} options={doughnutOptions} />
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2">
                <div className="text-center">
                  <p className="text-xs text-gray-500">Serviços</p>
                  <p className="text-sm font-bold">
                    {Math.round((servicesData.serviceRevenue / servicesData.totalRevenue) * 100)}% da receita
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs text-gray-500">Produtos</p>
                  <p className="text-sm font-bold">
                    {Math.round((servicesData.productRevenue / servicesData.totalRevenue) * 100)}% da receita
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
          
          {/* Lista de produtos */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            className="bg-white rounded-xl p-4 shadow-sm border border-gray-100"
          >
            <h3 className="text-lg font-semibold mb-4">Todos os Produtos</h3>
            
            <div className="overflow-x-auto">
              <table className="w-full min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Produto</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Preço</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Estoque</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Vendidos</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Faturamento</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Margem</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {products.map((product, index) => (
                    <tr key={product.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="font-medium text-gray-900">{product.name}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-900">R$ {product.price}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{product.stock} un</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-900">{product.sold} un</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="text-sm text-gray-900">R$ {product.revenue}</div>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                          {product.margin}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <div className="mt-4 p-3 bg-amber-50 rounded-lg">
              <p className="text-sm font-medium text-amber-800">Oportunidade de Cross-selling</p>
              <p className="text-xs text-amber-700 mt-1">
                Treine sua equipe para oferecer produtos durante e após os serviços. 
                A cada 10 clientes, apenas 2 adquirem produtos. O potencial é de aumentar 
                a venda de produtos em até 35%.
              </p>
            </div>
            
            <div className="mt-4 flex justify-end">
              <button className="text-sm text-green-600 font-medium hover:text-green-700 flex items-center">
                <span>Gerenciar produtos</span>
                <ArrowTrendingUpIcon className="h-4 w-4 ml-1 rotate-90" />
              </button>
            </div>
          </motion.div>
        </>
      )}
    </div>
  );
};

export default ServicesSection; 